<template>
	<feature-item :title="nested ? null : participationClass.name"
				  linkable
				  :subtitle="nested ? null : subtitle"
				  :value="`category-${participationClass._key}`" :divided="divided">

		<user-content v-if="participationClass.description" class="mb-16" small :class="{'mt-16': !nested}" :text="participationClass.description"/>

		<div :class="{'mt-16': !nested && !participationClass.description}">
			<info-list small>
				<info-list-item v-if="participationClass.status.value !== participationClassStatus.Published" :color="participationClass.status.color" :icon="icons.cancel"
								:text="participationClass.status.title"/>

				<info-list-item v-if="participationClass.is_virtual" :icon="icons.virtual" :help="$t(`enum.participation_class_type.${participationClass.type}.help`)">
					<a :href="`${$config.public.aboutUrl}/spoferan/virtual-events`" target="_blank">{{ $t(`enum.participation_class_type.${participationClass.type}.title`) }}</a>
				</info-list-item>

				<info-list-item :icon="icons.date" :text="$date(participationClass.start_date).formatDateTimeRange(participationClass.end_date)"/>

				<info-list-item
					v-if="participationClass.virtualSettings && (participationClass.virtualSettings.start_time || participationClass.virtualSettings.end_time)"
					:icon="icons.time"
					:text="$t('text.participation_start_possible_between', {start: participationClass.virtualSettings.start_time ? $date().time(participationClass.virtualSettings.start_time).format('HH:mm') : $date().startOf('day').format('HH:mm'), end: participationClass.virtualSettings.end_time ? $date().time(participationClass.virtualSettings.end_time).format('HH:mm') : $date().endOf('day').format('HH:mm')})"/>

				<info-list-item
					v-if="participationClass.has_registration && participationClass.register_start_date && participationClass.register_end_date && $date().isBefore(participationClass.end_date)"
					:icon="icons.register">
                    <span v-if="$date().isBefore(participationClass.register_start_date)">
                        {{ $t('text.participation_open_at', {start: $date(participationClass.register_start_date).formatDateTimeRange(false)}) }}
                    </span>
					<span v-else-if="$date().isBefore(participationClass.register_end_date)">{{
							$t('text.participation_open_until', {end: $date(participationClass.register_end_date).formatDateTimeRange(true)})
						}}</span>
					<span v-else class="text-warning">{{
							participationClass.register_end_date ? $t('text.registration_closed', {end: $date(participationClass.register_end_date).formatDateTimeRange(true)}) : $t('label.registration_closed')
						}}</span>
				</info-list-item>

				<info-list-item v-if="participationClass.goal_metric && goalData"
								:icon="goalData.icon"
								:text="`${$t(`enum.rank_metric.${participationClass.goal_metric}.goal_metric_title`)}: ${goalData.value}`"/>

				<info-list-item v-if="!nested && participationClass.participants_count && (hasActivePage(eventPageTypes.Participations) || hasActivePage(eventPageTypes.Results))"
								:icon="icons.participants">
					<nuxt-link :to="$web('events.participations.index', event._key)">
						{{ $t('param_label.registered_participants', participationClass.participants_count, {count: participationClass.participants_count}) }}
					</nuxt-link>
				</info-list-item>

			</info-list>

			<expansion-panels class="mt-8">
				<expansion-panel v-if="!nested && (teamModeKey || hasRestrictions)" :title="$t('label.registration_info')" small>
					<info-list small>

						<info-list-item v-if="teamModeKey && participationClass.teamSettings" :icon="icons.team_mode[participationClass.settings.team_mode]">
							<div class="flex flex-column">
								<span>{{ $t(`event_info.team_mode.${teamModeKey}.label`, {team_label: participationClass.teamSettings.label ?? $t('label.squad')}) }}</span>
								<span
									v-if="participationClass.teamSettings.min && participationClass.teamSettings.max && participationClass.teamSettings.min === participationClass.teamSettings.max">{{
										$t('param_label.participants_per_team', {count: participationClass.teamSettings.max, team_label: participationClass.teamSettings.label ?? $t('label.squad')})
									}}</span>
								<span v-else-if="participationClass.teamSettings.min && participationClass.teamSettings.max">{{
										$t('param_label.participants_per_team', {
											count: participationClass.teamSettings.min + ' - ' + participationClass.teamSettings.max, team_label: participationClass.teamSettings.label ?? $t('label.squad')
										})
									}}</span>
								<span v-else-if="participationClass.teamSettings.min">min. {{
										$t('param_label.participants_per_team', {
											count: participationClass.teamSettings.min, team_label: participationClass.teamSettings.label ?? $t('label.squad')})
									}}</span>
								<span v-else-if="participationClass.teamSettings.max">max. {{
										$t('param_label.participants_per_team', {
											count: participationClass.teamSettings.max, team_label: participationClass.teamSettings.label ?? $t('label.squad')})
									}}</span>
							</div>
						</info-list-item>

						<info-list-item v-if="participationClass.restrictions?.min_limit" :icon="icons.participants"
										:text="$t('restriction.min_limit', {count: participationClass.restrictions.min_limit, label: $t('label.participants')})"
										:help="$t('restriction.help.min_limit', {count: participationClass.restrictions.min_limit})"/>
						<info-list-item v-if="participationClass.restrictions?.limit" :icon="icons.participants"
										:text="$t('restriction.max_limit', {count: participationClass.restrictions.limit, label: $t('label.participants')})"/>

						<info-list-item v-if="teamModeKey && participationClass.restrictions?.team_min" :icon="icons.team_mode[2]"
										:text="$t('restriction.min_limit', {count: participationClass.restrictions.team_min, label: participationClass.teamSettings?.label ?? $t('label.squads')})"
										:help="$t('restriction.help.min_limit', {count: participationClass.restrictions.team_min})"/>
						<info-list-item v-if="teamModeKey && participationClass.restrictions?.team_max" :icon="icons.team_mode[2]"
										:text="$t('restriction.max_limit', {count: participationClass.restrictions.team_max, label: participationClass.teamSettings?.label ?? $t('label.squads')})"/>

						<info-list-item v-if="participationClass.restrictions && participationClass.restrictions.gender === 'm'" :icon="icons.male"
										:text="$t('restriction.participation_class.gender_male')"/>
						<info-list-item v-if="participationClass.restrictions && participationClass.restrictions.gender === 'f'" :icon="icons.female"
										:text="$t('restriction.participation_class.gender_female')"/>
						<info-list-item v-if="participationClass.restrictions && participationClass.restrictions.min_age" :icon="icons.age_class"
										:text="participationClass.restrictions.min_age_compare === 'event' ? $t('restriction.participation_class.min_age_event', {age: participationClass.restrictions.min_age}) : $t('restriction.participation_class.min_age_year', {age: participationClass.restrictions.min_age})"/>
						<info-list-item v-if="participationClass.restrictions && participationClass.restrictions.max_age" :icon="icons.age_class"
										:text="participationClass.restrictions.max_age_compare === 'event' ? $t('restriction.participation_class.max_age_event', {age: participationClass.restrictions.max_age}) : $t('restriction.participation_class.max_age_year', {age: participationClass.restrictions.max_age})"/>
						<info-list-item v-if="locationRestriction" :icon="icons.location" :text="$t('restriction.participation_class.location', {location: locationRestriction})"/>
					</info-list>
				</expansion-panel>
				<expansion-panel v-if="participationClass.ageClasses && participationClass.ageClasses.length" :title="$t('label.age_classes')" small>
					<info-list small>
						<info-list-item v-for="(ageClass, ageClassKey) in participationClass.ageClasses" :key="'age-classes-' + ageClassKey" :icon="false">
                                <span><b class="font-heading">{{ ageClass.name }}: </b>
                            <span v-if="ageClass.gender === 'm'">{{ $t('label.men') }}&nbsp;</span>
                            <span v-else-if="ageClass.gender === 'f'">{{ $t('label.women') }}&nbsp;</span>

                            <span v-if="ageClass.max_age && ageClass.max_age === ageClass.min_age">{{ ageClass.min_age }}</span>
                            <span v-else-if="ageClass.max_age && ageClass.min_age">{{ ageClass.min_age }} - {{ ageClass.max_age }}</span>
                            <span v-else-if="ageClass.min_age">{{ $t('conjunction.starting_at') }} {{ ageClass.min_age }}</span>
                            <span v-else-if="ageClass.max_age">{{ $t('conjunction.until') }} {{ ageClass.max_age }}</span>
                            {{ $t('label.years') }}</span>
						</info-list-item>
					</info-list>
				</expansion-panel>
				<expansion-panel v-if="participationClass.settings.rank_mode === 4" :title="$t('label.participation_information')" small>

					<info-list
						v-if="participationClass.virtualSettings && (!participationClass.virtualSettings.max_attempts || participationClass.virtualSettings.max_attempts > 1)" small
						class="mb-16">
						<info-list-item
							:icon="icons.attempts"
							:help="$t('help.participation_class.max_attempts_possible')"
							:text="participationClass.virtualSettings.max_attempts ? $t('label.max_attempts_possible', {attempts: participationClass.virtualSettings.max_attempts}) : $t('label.arbitrary_max_attempts')"/>
					</info-list>

					<template v-if="participationClass.settings.supportedTrackerTypes.length">
						<p>{{ $t('text.event.virtual', {event: participationClass.name }) }}</p>

						<row tight>
							<column cols="12" sm="6" v-for="trackerType in participationClass.settings.supportedTrackerTypes" :key="trackerType.value">
								<tooltip with-touch top :text="trackerType.description">
									<document-item :title="trackerType.title"
												   :icon="trackerType.icon"
												   :icon-width="20">
										<template v-if="trackerType.image" #icon>
											<cdn-image :size="{width: 20}" :ratio="1" :src="trackerType.image"/>
										</template>
										<template #suffix>
											<icon :icon="icons.help" x-small color="gray-400" class="ml-16"/>
										</template>
									</document-item>
								</tooltip>
							</column>
						</row>
					</template>

				</expansion-panel>
				<expansion-panel v-if="rankModeKey || hasAdvancedInfo || rankKeyData" :title="$t('label.advanced_information')" small>
					<info-list small>

						<info-list-item v-if="rankKeyData"
										:icon="rankKeyData.icon"
										:help="$t(`enum.rank_metric.${rankKeyData.metric}.rank_metric_help`)"
										:text="$t(`enum.rank_metric.${rankKeyData.metric}.rank_metric_title`)"/>

						<info-list-item v-if="rankModeKey" :icon="icons.rank"
										:text="$t(`event_info.rank_mode.${rankModeKey}.label`)"
										:help="$t(`event_info.rank_mode.${rankModeKey}.help`)"/>

						<template v-if="participationClass.info">
						<template v-for="(booleanInfo, infoKey) in participationClass.info" >
							<info-list-item v-if="booleanInfo === true || booleanInfo === '1'" :key="'info-' + infoKey"
											:icon="icons[infoKey] ? icons[infoKey] : icons.info" :help="$t('event_info.'+infoKey+'.label.help')"
											:text="$t('event_info.'+infoKey+'.label.label')"/>
						</template>
						</template>

					</info-list>
				</expansion-panel>

				<expansion-panel v-if="hasActivePage(eventPageTypes.Map)" :title="$t('label.map')" small :to="{path: `/events/${event._key}/map`, query: {class: participationClass._key}}"/>

			</expansion-panels>
		</div>

		<template #info>
			<div class="flex flex-column justify-between sm:h-full w-full sm:w-auto">
				<div v-if="!nested" class="flex flex-column items-end justify-end">
					<template v-if="participationClass.minDisplayablePrice">
						<template v-if="participationClass.minDisplayablePrice.cents > 0 || participationClass.maxDisplayablePrice?.cents > 0 || participationClass.minNextPrice">

							<template v-if="participationClass.maxDisplayablePrice?.cents !== participationClass.minDisplayablePrice.cents">
								<p v-if="participationClass.teamSettings?.fee_charge_mode === 'team'"
								   class="text-success mb-0 text-sub leading-normal text-right text-sm md:text-md" v-html="$t('label.start_end_price_per_unit', {start:
                        toEuro(participationClass.minDisplayablePrice.cents), end: toEuro(participationClass.maxDisplayablePrice.cents), unit: participationClass.teamSettings.label ?? $t('label.squad')})"/>
								<p v-else class="text-success mb-0 text-sub leading-normal text-right text-sm md:text-md" v-html=" $t('label.start_end_price_per_unit', {start:
                        toEuro(participationClass.minDisplayablePrice.cents), end: toEuro(participationClass.maxDisplayablePrice.cents), unit: $t('label.person')})"/>
							</template>
							<template v-else>
								<p v-if="participationClass.teamSettings?.fee_charge_mode === 'team'"
								   class="text-success mb-0 text-sub leading-normal text-right text-sm md:text-md" v-html="participationClass.minNextPrice ? $t('label.start_price_per_unit', {price:
                        toEuro(participationClass.minDisplayablePrice.cents), unit: participationClass.teamSettings.label ?? $t('label.squad')}) : $t('label.price_per_unit', {price: toEuro(participationClass.minDisplayablePrice.cents), unit: participationClass.teamSettings.label ?? $t('label.squad')})"/>
								<p v-else class="text-success mb-0 text-sub leading-normal text-right text-sm md:text-md" v-html="participationClass.minNextPrice ? $t('label.start_price_per_unit', {price:
                        toEuro(participationClass.minDisplayablePrice.cents), unit: $t('label.person')}) : $t('label.price_per_unit', {price: toEuro(participationClass.minDisplayablePrice.cents), unit: $t('label.person')})"/>
							</template>

							<p class="text-soft text-4xs leading-tight mb-0">{{
									participationClass.minDisplayablePrice.tax_percent ? $t('text.including_tax_and_fees', {tax: participationClass.minDisplayablePrice.tax_percent}) : $t('text.including_fees')
                            }}</p>
						</template>
						<p class="text-success text-sub mb-0 text-right text-uppercase text-sm md:text-md" v-else>{{ $t('label.free') }}</p>

						<template v-if="participationClass.minDisplayablePrice.is_active && $date().isBefore(participationClass.register_end_date)">
							<p v-if="participationClass.minDisplayablePrice.available_purchases_count"
							   class="text-4xs text-warning leading-tight mt-4 mb-0">{{
									participationClass.minDisplayablePrice.max_purchases === participationClass.minDisplayablePrice.available_purchases_count ? $t('param_text.valid_for_the_next_count_purchases', participationClass.minDisplayablePrice.available_purchases_count, {count: participationClass.minDisplayablePrice.available_purchases_count}) : $t('param_text.valid_for_count_remaining_purchases', participationClass.minDisplayablePrice.available_purchases_count, {count: participationClass.minDisplayablePrice.available_purchases_count})
                            }}</p>

							<p v-if="participationClass.minNextPrice && participationClass.minDisplayablePrice.end_date && participationClass.minNextPrice.cents > participationClass.minDisplayablePrice.cents"
							   class="text-4xs text-warning leading-tight mt-2 mb-0">{{
									$t('text.price.increase', {date: $date(participationClass.minNextPrice.start_date ?? participationClass.minDisplayablePrice.end_date).add(1, 'minute').format('LL')}) }}</p>
						</template>

						<span class="text-primary text-2xs cursor-pointer leading-tight mt-5" @click.prevent="showPriceDetailsModal = true">{{ $t('label.details') }}</span>

						<lazy-modal v-model="showPriceDetailsModal" :title="participationClass.name" small>

							<p class="subheading text-sm text-left mb-16">{{ $t('label.participation_prices') }}</p>

							<data-loader class="w-full" :url="$api('events.prices.index', event._key)"
										 as-guest
									   :params="{participation_class: participationClass.id, order: ['for_additional', 'cents'], dir: ['asc', 'asc']}">

								<template #loading>
									<loader/>
								</template>

								<template v-slot="{data}">
									<template v-for="(price, priceIndex) in data" :key="`price-${price.id}`">

										<template v-if="price.for_additional && (priceIndex === 0 || !data[priceIndex-1].for_additional)">
											<p class="subheading text-sm text-left mb-16">{{ $t('label.additional_participation_attempt_prices') }}</p>
										</template>

										<lazy-price :price="price"
													:set-prices="data"
													:inactive="!price.for_additional && !price.is_active"
													:active="!price.for_additional && price.is_active"/>
									</template>
								</template>
							</data-loader>

							<divider margin="16"/>
							<p class="subheading text-sm mb-16 text-left">{{ $t('label.cancel_conditions') }}</p>

							<data-loader class="w-full" as-guest :url="$api('events.cancel_conditions.index', event._key)" :params="{participation_class: participationClass.id}">

								<template #loading>
									<loader/>
								</template>

								<template v-slot="{data}">
									<lazy-cancel-condition v-for="(cancelCondition, index) in data" :cancel-condition="cancelCondition"
														   :active="cancelCondition.is_active"
														   :inactive="!cancelCondition.is_active"
														   :key="'cancel-condition-' + index"/>
								</template>

								<template #missing>
									<missing-info :text="$t('missing.participation_class.cancel_conditions')" no-spacing/>
								</template>

							</data-loader>

						</lazy-modal>
					</template>

					<div
						v-if="$date().isAfter(participationClass.register_start_date) && $date().isBefore(participationClass.register_end_date) && !nested && participationClass.status.value === participationClassStatus.Published"
						class="border-t mt-12 pt-12">
						<btn v-if="participationClass.has_registration && !event.managed && event.external_url" color="success" class="w-200 max-w-full" :to="event.external_url"
							 target="_blank">
							<icon :icon="icons.external_link"/>
							<span>{{ $t('action.participate_now') }}!</span>
						</btn>
						<template v-else-if="participationClass.has_registration && participationClass.minDisplayablePrice?.is_active">
							<btn
								v-if="participationClass.participants_count && participationClass.restrictions?.limit && (participationClass.restrictions.limit - participationClass.participants_count) <= 0"
								color="warning" disabled class="w-200 max-w-full flex flex-column">{{ $t('text.sold_out_participants_count') }}
							</btn>
							<btn v-else color="success" class="w-200 max-w-full flex flex-column"
								 :to="{path: $web('events.participations.create', event._key), query: {class: participationClass._key}}">
								<span>{{ $t('action.register_now') }}!</span>
								<span v-if="participationClass.participants_count && participationClass.restrictions?.limit" class="text-4xs">
                                {{ $t('text.remaining_participants_count', {count: participationClass.restrictions.limit - participationClass.participants_count}) }}
                            </span>
							</btn>
						</template>
					</div>

				</div>
				<div v-if="!nested && (participationClass.featureElements?.length || participationClass.ads?.length)"
					 class="sm:w-260 lg:w-380 p-4 mt-16">
					<lazy-event-feature-element-card v-if="participationClass.featureElements?.length || participationClass.ads?.length"
													 :elements="participationClass.featureElements" :ads="participationClass.ads" :event="event" no-spacing/>
				</div>
			</div>
		</template>

	</feature-item>
</template>

<script>
import {
    mdiAccount,
    mdiAccountGroup,
    mdiAccountMultiple,
    mdiAccountMultipleOutline,
    mdiCellphoneNfc,
    mdiElevationRise,
    mdiHumanFemale,
    mdiHumanMale,
    mdiHumanMaleGirl,
    mdiLogin,
    mdiMapMarkerDistance,
    mdiRepeat,
    mdiTimerOutline,
    mdiTrophy
} from '@mdi/js';
import {ParticipationClassStatus} from "@spoferan/spoferan-ts-core";
import {getParticipationClassRankModeLabel} from "@spoferan/spoferan-ts-core";
import {getParticipationClassTeamModeLabel} from "@spoferan/spoferan-ts-core";
import {toEuro} from "@spoferan/spoferan-ts-core";
import {getAddressText} from "@spoferan/spoferan-ts-core";
import {RankMetric} from "@spoferan/spoferan-ts-core";
import StoreEventMixin from "../../mixins/StoreEventMixin";
import {EventPageType} from "@spoferan/spoferan-ts-core";
import {externalLinkIcon, dateIcon, infoIcon, locationIcon, helpIcon, nextIcon} from "@spoferan/nuxt-spoferan/icons";
import {cancelIcon} from "@spoferan/nuxt-spoferan/icons";
import {QmnAds} from "../../config/qmn";

export default {
    name: 'ParticipationClass',

    mixins: [StoreEventMixin],

    props: {

        participationClass: {
            type: Object,
            required: true
        },

        divided: {
            type: Boolean,
            default: false
        },

        // Whether the participation class is nested within a group whereby specific elements are not displayed
        nested: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            eventPageTypes: EventPageType,
			participationClassStatus: ParticipationClassStatus,
            showPriceDetailsModal: false,
            icons: {
				external_link: externalLinkIcon,
				location: locationIcon,
				help: helpIcon,
				next: nextIcon,
                participants: mdiAccountGroup,
                male: mdiHumanMale,
                female: mdiHumanFemale,
                age_class: mdiHumanMaleGirl,
                attempts: mdiRepeat,
                rank: mdiTrophy,
                altitude: mdiElevationRise,
                distance: mdiMapMarkerDistance,
                time: mdiTimerOutline,
                register: mdiLogin,
                virtual: mdiCellphoneNfc,
				date: dateIcon,
				info: infoIcon,
				cancel: cancelIcon,
                team_mode: {
                    0: mdiAccount,
                    1: mdiAccountMultipleOutline,
                    2: mdiAccountMultiple
                },
            }
        }
    },

    computed: {
		QmnAds() {
			return QmnAds
		},

        subtitle() {
            if (this.nested || !this.participationClass.sportType) {
                return null;
            }

            let subtitle = this.participationClass.sportType.title;

            if (this.participationClass.format) {
                subtitle += ` | ${this.participationClass.format.title}`;
            }

            return subtitle;
        },

        hasRestrictions() {
            return this.participationClass.restrictions && Object.keys(this.participationClass.restrictions).length && Object.keys(this.participationClass.restrictions).some(restrictionKey => {
                return [null, undefined, ''].indexOf(this.participationClass.restrictions[restrictionKey]) < 0;
            });
        },

        hasAdvancedInfo() {
            return this.participationClass.info && Object.keys(this.participationClass.info).length && Object.keys(this.participationClass.info).some(infoKey => {
                return [null, undefined, ''].indexOf(this.participationClass.info[infoKey]) < 0;
            });
        },

        // The location restriction to show on the restriction list
        locationRestriction() {
            if (this.participationClass.restrictions && this.participationClass.restrictions.origin && Object.keys(this.participationClass.restrictions.origin).length && this.participationClass.restrictions.origin.country_code) {
                return getAddressText(this.participationClass.restrictions.origin)
            }

            return null
        },

        rankModeKey() {
            return this.participationClass.settings.rank_mode ? getParticipationClassRankModeLabel(this.participationClass.settings.rank_mode) : null
        },

        teamModeKey() {
            return this.participationClass.settings.team_mode ? getParticipationClassTeamModeLabel(this.participationClass.settings.team_mode) : null
        },

        rankKeyData() {
            if (!this.rankModeKey || !this.participationClass.rank_metric) {
                return null;
            }
            let icon;
            switch (this.participationClass.rank_metric) {
                case RankMetric.Time:
                    icon = this.icons.time;
                    break;
                case RankMetric.Distance:
                    icon = this.icons.distance;
                    break;
                case RankMetric.Altitude:
                    icon = this.icons.altitude;
                    break;
            }
            return {
                metric: this.participationClass.rank_metric,
                icon: icon
            }
        },

        goalData() {
            if (!this.participationClass.goal_metric || !this.participationClass.goal_value || this.participationClass.goal_value === '0') {
                return null;
            }

            switch (this.participationClass.goal_metric) {
                case RankMetric.Time:
                    return {
                        value: this.getReadableTimeDuration(this.participationClass.goal_value),
                        icon: this.icons.time
                    }
                case RankMetric.Distance:
                    return {
                        value: this.participationClass.goal_value < 1000 ? `${this.participationClass.goal_value} ${this.$t('label.meters')}` : `${(this.participationClass.goal_value / 1000).toLocaleString(this.$i18n?.localeProperties?.iso ?? 'de-DE', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                        })} ${this.$t('label.kilometers')}`,
                        icon: this.icons.distance
                    }
                case RankMetric.Altitude:
                    return {
                        value: `${this.participationClass.goal_value} ${this.$t('label.meters')}`,
                        icon: this.icons.altitude
                    }
            }
        }
    },

    methods: {
        toEuro(val) {
            return toEuro(val);
        },

        getReadableTimeDuration(timeValue) {
            if (!timeValue) {
                return '';
            }

            const timeValueParts = timeValue.split(':');
            const hours = parseInt(timeValueParts[0]);
            const minutes = parseInt(timeValueParts.length > 1 ? timeValueParts[1] : 0);
            const seconds = parseInt(timeValueParts.length > 2 ? timeValueParts[2] : 0);
            const readableParts = [];
            if (hours) {
                readableParts.push(this.$t('param_label.hours', hours, {count: hours}));
            }
            if (minutes) {
                readableParts.push(this.$t('param_label.minutes', minutes, {count: minutes}));
            }
            if (seconds) {
                readableParts.push(this.$t('param_label.seconds', seconds, {count: seconds}));
            }
            return readableParts.join(' ');
        }
    }
}
</script>
